import {useEffect, useState} from "react";
import {PricingApi} from "pricing/PricingApi";
import PricingStorage, {MONTHLY, type PaidPlan} from "pricing/PricingStorage";
import {pricingCountryForContext} from "pricing/planCatalogue";
import {useLocale} from "i18n";
import {storefrontLocale} from "i18n/localePreference";
import {UpsellApi, type UpsellOffer} from "./UpsellApi";

/**
 * Asks the api what this buyer may be told about a plan's MONTHLY price
 * (kitecorp/transferify#117, api kitecorp/transferify-api-service#838).
 *
 * Thin adapter: it schedules the ask and holds the answer. Whether the answer becomes an offer is
 * decided by the React-free rules in `./firstMonthOffer`.
 *
 * <b>MONTHLY is not a default that can be overridden.</b> The coupon is monthly-only and the api
 * refuses to quote a yearly price at all, so asking about one would be asking a question whose answer
 * is always "no offer" — this hook never forms that question, and the api would refuse it anyway.
 * Neither half is sufficient on its own and both are deliberate.
 *
 * @param plan   the tier being pitched, or null when there is nothing to pitch
 * @param active false suppresses the ask entirely — the moment has not happened, or the reader is
 *               already a subscriber. The api's live Stripe coupon read is bounded by offers actually
 *               shown, so a speculative ask is a real cost, not just a wasted round trip.
 */
export function useFirstMonthOffer(plan: PaidPlan | null, active: boolean): UpsellOffer | null {
	return useFirstMonthOfferState(plan, active).offer;
}

/** Exposes settlement for mutually exclusive placements that must not show a lower-priority offer first. */
export function useFirstMonthOfferState(plan: PaidPlan | null, active: boolean) {
	// The WHOLE offer, not just its percentage (kitecorp/transferify-api-service#933): the campaign's
	// occasion and closing day arrive on the same response, and a second hook fetching them would be a
	// second live Stripe read for one question. Callers that want only the number read `.percentOff`.
	const {locale} = useLocale();
	const [offer, setOffer] = useState<UpsellOffer | null>(null);
	const [loading, setLoading] = useState(active && plan !== null);

	useEffect(() => {
		if (!active || plan === null) {
			setOffer(null);
			setLoading(false);
			return;
		}
		let disposed = false;
		setLoading(true);
		void (async () => {
			try {
				// Refetched rather than read from storage, for the same reason the checkout refetches:
				// locale/domain currency must never inherit a stale cache, and the price id we ask about
				// has to be the very one the CTA would buy.
				const {products} = await PricingApi.getPlan(
					pricingCountryForContext(storefrontLocale(), window.location.hostname));
				PricingStorage.setProducts(products);
				const price = PricingStorage.priceFor(plan, MONTHLY);
				if (disposed || !price?.id) {
					return;
				}
				const quoted = await UpsellApi.getOffer(price.id);
				if (!disposed) {
					setOffer(quoted);
				}
			} catch {
				// A promotions lookup that fails is not an error a reader should see: the line simply does
				// not appear. Retrying would spend another live Stripe read for a sentence nobody asked
				// for, and there is no state here worth recovering.
				if (!disposed) {
					setOffer(null);
				}
			} finally {
				if (!disposed) setLoading(false);
			}
		})();
		return () => {
			disposed = true;
		};
		// Keyed on the reader's LANGUAGE, which a switch moves, so the answer is re-fetched for the
		// catalogue they have just chosen. The VALUE used is read live inside the effect (above), so the
		// dependency is a change signal rather than the input — `setStoredLocale` refreshes the cache the
		// live read consults before this re-runs.
	}, [plan, active, locale]);

	return {offer, loading};
}
